import { assign, createMachine } from "xstate";
import { addAxiosErrorToContextAction } from "./actions/addAxiosErrorToContextAction";
import { addBookingValuesAction } from "./actions/addBookingValuesAction";
import { addValidationErrorAction } from "./actions/addValidationErrorAction";
import { clearValidationErrorAction } from "./actions/clearValidationErrorAction";
import { setBookingTypeAction } from "./actions/setBookingTypeAction";
import { setDepotAction } from "./actions/setDepotAction";
import { setNameAndDepotAction } from "./actions/setNameAndDepotAction";
import { setSelectableDepotsAction } from "./actions/setSelectableDepotsAction";
import { setSelectedCurrencyAction } from "./actions/setSelectedCurrencyAction";
import { loadDepots } from "./services/loadDepots";
import { persistBooking } from "./services/persistBooking";
import { persistDepot } from "./services/persistDepot";
import { validateBooking } from "./services/validateBooking";
import { TransactionWizardState } from "./types/transactionWizardState";
import {
  defaultContext,
  TransactionWizardContext,
} from "./types/transactionWizardContext";
import { TransactionWizardEvent } from "./types/TransactionWizardEvent";
import { initFromInvestmentTypeAction } from "./actions/initFromInvestmentTypeAction";

export const manualTransactionWizardSM = createMachine<
  TransactionWizardContext,
  TransactionWizardEvent,
  TransactionWizardState
>(
  {
    id: "manualTransactionWizardSM",

    // Initial state
    initial: "idle",

    // Local context for entire machine
    context: defaultContext,

    // State definitions
    states: {
      idle: {
        on: {
          INIT_FROM_BOOKING_TYPE: {
            target: "selectBookingType",
            actions: ["setDepotAction", "initFromInvestmentTypeAction"],
          },
          INIT_FROM_INVESTMENT_TYPE: {
            target: "selectInvestmentType",
            actions: ["setDepotAction"],
          },
        },
      },
      selectInvestmentType: {
        on: {
          INVESTMENT_TYPE_SELECTED: {
            target: "selectBookingType",
            actions: ["initFromInvestmentTypeAction"],
          },
        },
      },
      selectBookingType: {
        id: "selectBookingType",
        on: {
          BOOKING_TYPE_SELECTED: {
            target: "depots",
            actions: ["setBookingTypeAction"],
          },
          BACK: { target: "selectInvestmentType" },
        },
      },
      depots: {
        initial: "loadDepots",
        states: {
          loadDepots: {
            invoke: {
              id: "loadDepots",
              src: loadDepots,
            },
            on: {
              DEPOTS_LOADED: { target: "selectDepot" },
              SOMETHING_WRONG: { target: "#somethingWrong" },
            },
          },
          selectDepot: {
            entry: ["setSelectableDepotsAction"],
            on: {
              NAME_AND_DEPOT_SELECTED: {
                target: "#priceAndDate",
                actions: ["setNameAndDepotAction"],
              },
              CREATE_NEW_DEPOT: {
                target: "createNewDepot",
                actions: ["setNameAndDepotAction"],
              },
              BACK: { target: "#selectBookingType" },
            },
          },
          createNewDepot: {
            on: {
              PERSIST_DEPOT: { target: "persistNewDepot" },
              BACK: { target: "selectDepot" },
            },
          },
          persistNewDepot: {
            invoke: {
              id: "persistDepot",
              src: persistDepot,
            },
            on: {
              DEPOT_PERSISTED: { target: "loadDepots" },
            },
            exit: ["setDepotAction"],
          },
        },
      },
      priceAndDate: {
        id: "priceAndDate",
        on: {
          VALIDATE_BOOKING: {
            target: "validateBooking",
            actions: ["addBookingValuesAction", "setSelectedCurrencyAction"],
          },
          SOMETHING_WRONG: { target: "somethingWrong" },
          BACK: { target: "depots" },
        },
      },
      validateBooking: {
        invoke: {
          id: "validateBooking",
          src: validateBooking,
        },
        on: {
          BOOKING_VALID: [
            {
              target: "persist",
              actions: ["clearValidationErrorAction"],
              cond: (context) => Boolean(context.skipSummaryStep),
            },
            {
              target: "summary",
              actions: ["clearValidationErrorAction"],
            },
          ],
          BOOKING_INVALID: {
            target: "priceAndDate",
            actions: ["addValidationErrorAction"],
          },
          SOMETHING_WRONG: { target: "somethingWrong" },
        },
      },
      summary: {
        on: {
          PERSIST: { target: "persist" },
          BACK: { target: "priceAndDate" },
        },
      },
      persist: {
        invoke: {
          id: "persistBooking",
          src: persistBooking,
        },
        on: {
          BOOKING_VALID: {
            target: "persisted",
          },
          BOOKING_INVALID: {
            target: "priceAndDate",
            actions: ["addValidationErrorAction"],
          },
          SOMETHING_WRONG: { target: "somethingWrong" },
        },
      },
      persisted: {
        type: "final",
      },
      somethingWrong: {
        type: "final",
        id: "somethingWrong",
        entry: ["addAxiosErrorToContextAction"],
      },
    },
  },
  {
    actions: {
      initFromInvestmentTypeAction: assign(initFromInvestmentTypeAction),
      setSelectedCurrencyAction: assign(setSelectedCurrencyAction),
      setSelectableDepotsAction: assign(setSelectableDepotsAction),
      setBookingTypeAction: assign(setBookingTypeAction),
      setNameAndDepotAction: assign(setNameAndDepotAction),
      setDepotAction: assign(setDepotAction),
      addBookingValuesAction: assign(addBookingValuesAction),
      addValidationErrorAction: assign(addValidationErrorAction),
      clearValidationErrorAction: assign(clearValidationErrorAction),
      addAxiosErrorToContextAction: assign(addAxiosErrorToContextAction),
    },
  }
);
